import { useState, useEffect, useCallback, useMemo } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { 
  ChevronDown, 
  ChevronUp, 
  Trash2, 
  Plus,
  GripVertical,
  RefreshCw,
  Flame,
  Calculator,
  Copy,
  ArrowRight,
  Bookmark,
  BookmarkPlus,
  RefreshCcw,
  Layers
} from "lucide-react";
import { cn } from "../../lib/utils";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback static categories
const FALLBACK_CATEGORIES = {
  proteinas: { label: "Proteínas", color: "text-red-400", bgColor: "bg-red-500/10" },
  carbohidratos: { label: "Carbohidratos", color: "text-yellow-400", bgColor: "bg-yellow-500/10" },
  grasas_saludables: { label: "Grasas saludables", color: "text-blue-400", bgColor: "bg-blue-500/10" },
  grasas: { label: "Grasas", color: "text-blue-400", bgColor: "bg-blue-500/10" },
  verduras: { label: "Verduras", color: "text-green-400", bgColor: "bg-green-500/10" },
  frutas: { label: "Frutas", color: "text-orange-400", bgColor: "bg-orange-500/10" },
  lacteos: { label: "Lácteos", color: "text-cyan-400", bgColor: "bg-cyan-500/10" },
  legumbres: { label: "Legumbres", color: "text-amber-400", bgColor: "bg-amber-500/10" },
  suplementos: { label: "Suplementos", color: "text-purple-400", bgColor: "bg-purple-500/10" },
  otros: { label: "Otros", color: "text-gray-400", bgColor: "bg-gray-500/10" },
  custom: { label: "Manual", color: "text-gray-400", bgColor: "bg-gray-500/10" },
};

const CATEGORY_ORDER = [
  "proteinas", "carbohidratos", "grasas_saludables", "verduras", 
  "frutas", "lacteos", "legumbres", "suplementos", "otros"
];

const getCategoryInfo = (categoryId) => {
  return FALLBACK_CATEGORIES[categoryId] || FALLBACK_CATEGORIES.otros;
};

// Calculate macros for a food item based on amount
const calculateItemMacros = (item, foodsLibrary) => {
  if (item.manual_macros) {
    return {
      calories: item.manual_macros.calories || 0,
      protein: item.manual_macros.protein || 0,
      carbs: item.manual_macros.carbs || 0,
      fat: item.manual_macros.fat || 0
    };
  }

  if (item.category === 'custom' || item.food_id === 'custom') {
    return { calories: 0, protein: 0, carbs: 0, fat: 0 };
  }

  const categoryFoods = foodsLibrary[item.category] || [];
  const food = categoryFoods.find(f => f.id === item.food_id);
  
  if (!food) {
    return { calories: 0, protein: 0, carbs: 0, fat: 0 };
  }

  const amount = item.amount || 0;
  let multiplier = amount / 100;
  
  if (item.unit === 'uds') {
    const defaultGramsPerUnit = food.default_amount || 100;
    multiplier = (amount * defaultGramsPerUnit) / 100;
  }

  return {
    calories: Math.round((food.calories_per_100 || 0) * multiplier),
    protein: Math.round((food.protein_per_100 || 0) * multiplier * 10) / 10,
    carbs: Math.round((food.carbs_per_100 || 0) * multiplier * 10) / 10,
    fat: Math.round((food.fat_per_100 || 0) * multiplier * 10) / 10
  };
};

// Calculate total macros for a list of items
const calculateTotalMacros = (items, foodsLibrary) => {
  return items.reduce((total, item) => {
    const itemMacros = calculateItemMacros(item, foodsLibrary);
    return {
      calories: total.calories + itemMacros.calories,
      protein: Math.round((total.protein + itemMacros.protein) * 10) / 10,
      carbs: Math.round((total.carbs + itemMacros.carbs) * 10) / 10,
      fat: Math.round((total.fat + itemMacros.fat) * 10) / 10
    };
  }, { calories: 0, protein: 0, carbs: 0, fat: 0 });
};

// Meal totals summary component
const MealTotalsSummary = ({ itemsA, itemsB, foodsLibrary, onCopyAtoB }) => {
  const totalsA = useMemo(() => calculateTotalMacros(itemsA, foodsLibrary), [itemsA, foodsLibrary]);
  const totalsB = useMemo(() => calculateTotalMacros(itemsB, foodsLibrary), [itemsB, foodsLibrary]);
  
  return (
    <div className="p-3 bg-[#0B0B0B] rounded-lg border border-[#2A2A2A] space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-[#D4AF37] text-xs font-semibold flex items-center gap-1">
          <Calculator className="w-3 h-3" /> TOTALES COMIDA
        </span>
        {/* Copy A to B button */}
        {onCopyAtoB && itemsA.length > 0 && (
          <button
            onClick={onCopyAtoB}
            className="flex items-center gap-1 px-2 py-1 text-[10px] bg-[#2A2A2A] hover:bg-[#3A3A3A] text-[#888] hover:text-white rounded transition-colors"
            title="Copiar Opción A a Opción B"
          >
            <Copy className="w-3 h-3" />
            <span>A → B</span>
          </button>
        )}
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        {/* Option A totals */}
        <div className="p-2 bg-[#D4AF37]/5 rounded border border-[#D4AF37]/20">
          <div className="text-[10px] text-[#D4AF37] font-medium mb-1">OPCIÓN A</div>
          <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-[11px]">
            <span className="text-[#D4AF37] font-bold">{totalsA.calories} kcal</span>
            <span className="text-red-400">{totalsA.protein}g P</span>
            <span className="text-yellow-400">{totalsA.carbs}g C</span>
            <span className="text-blue-400">{totalsA.fat}g G</span>
          </div>
        </div>
        
        {/* Option B totals */}
        <div className="p-2 bg-[#888]/5 rounded border border-[#888]/20">
          <div className="text-[10px] text-[#888] font-medium mb-1">OPCIÓN B</div>
          <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-[11px]">
            <span className="text-[#888] font-bold">{totalsB.calories} kcal</span>
            <span className="text-red-400">{totalsB.protein}g P</span>
            <span className="text-yellow-400">{totalsB.carbs}g C</span>
            <span className="text-blue-400">{totalsB.fat}g G</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Equivalents modal component
const EquivalentsModal = ({ open, onClose, item, foodsLibrary, onSelectEquivalent }) => {
  const [equivalents, setEquivalents] = useState([]);
  const [loading, setLoading] = useState(false);
  const { getAuthHeaders } = useAuth();

  useEffect(() => {
    if (open && item?.food_id && item.food_id !== 'custom') {
      loadEquivalents();
    }
  }, [open, item]);

  const loadEquivalents = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/foods/${item.food_id}/equivalents`,
        { headers: getAuthHeaders() }
      );
      setEquivalents(response.data.equivalents || []);
    } catch (error) {
      console.error("Error loading equivalents:", error);
    } finally {
      setLoading(false);
    }
  };

  const categoryFoods = foodsLibrary[item?.category] || [];
  const currentFood = categoryFoods.find(f => f.id === item?.food_id);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#121212] border-[#2A2A2A] text-white max-w-md max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#D4AF37] flex items-center gap-2">
            <RefreshCcw className="w-5 h-5" />
            Sustituciones Equivalentes
          </DialogTitle>
        </DialogHeader>
        
        {currentFood && (
          <div className="p-3 bg-[#1A1A1A] rounded-lg border border-[#2A2A2A] mb-4">
            <div className="text-sm font-medium text-white mb-1">{currentFood.name}</div>
            <div className="text-xs text-[#666]">
              {item.amount}{item.unit} → {calculateItemMacros(item, foodsLibrary).calories} kcal
            </div>
          </div>
        )}

        <div className="text-xs text-[#888] mb-2">
          Alimentos con macros similares:
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-5 h-5 text-[#D4AF37] animate-spin" />
          </div>
        ) : equivalents.length === 0 ? (
          <div className="text-center py-8 text-[#666] text-sm">
            No se encontraron sustituciones
          </div>
        ) : (
          <div className="space-y-2">
            {equivalents.map((eq, idx) => (
              <button
                key={idx}
                onClick={() => {
                  onSelectEquivalent({
                    food_id: eq.food.id,
                    category: eq.food.category,
                    amount: eq.equivalent_amount,
                    unit: eq.unit
                  });
                  onClose();
                }}
                className="w-full p-3 bg-[#1A1A1A] hover:bg-[#2A2A2A] rounded-lg border border-[#2A2A2A] text-left transition-colors"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-white text-sm font-medium">{eq.food.name}</span>
                  <span className="text-[10px] text-green-400 bg-green-500/10 px-2 py-0.5 rounded">
                    {eq.similarity_score}% similar
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-[#888]">{eq.equivalent_amount}{eq.unit}</span>
                  <span className="text-[#444]">→</span>
                  <span className="text-[#D4AF37]">{eq.macros_at_equivalent.calories} kcal</span>
                  <span className="text-red-400">{eq.macros_at_equivalent.protein}P</span>
                  <span className="text-yellow-400">{eq.macros_at_equivalent.carbs}C</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Food Item Editor with equivalents support
const FoodItemEditor = ({ item, onChange, onDelete, foodsLibrary }) => {
  const [showManualMacros, setShowManualMacros] = useState(!!item.manual_macros);
  const [showEquivalents, setShowEquivalents] = useState(false);
  const category = getCategoryInfo(item.category);
  
  const categoryFoods = foodsLibrary[item.category] || [];
  const selectedFood = categoryFoods.find(f => f.id === item.food_id);
  
  const itemMacros = useMemo(() => 
    calculateItemMacros(item, foodsLibrary), 
    [item, foodsLibrary]
  );
  
  const isManualEntry = item.category === 'custom' || item.food_id === 'custom';
  const isLegacyItem = item.food_id && !selectedFood && !isManualEntry;
  
  const handleManualMacroChange = (field, value) => {
    const newManualMacros = {
      ...(item.manual_macros || {}),
      [field]: parseFloat(value) || 0
    };
    onChange({ ...item, manual_macros: newManualMacros });
  };

  const clearManualMacros = () => {
    const { manual_macros, ...rest } = item;
    onChange(rest);
    setShowManualMacros(false);
  };

  const handleSelectEquivalent = (equivalentData) => {
    onChange({
      ...item,
      ...equivalentData,
      manual_macros: undefined
    });
    toast.success("Alimento sustituido");
  };
  
  return (
    <div className="p-2 bg-[#0B0B0B] rounded-lg border border-[#2A2A2A] space-y-2">
      <div className="flex items-center gap-2">
        {/* Categoría */}
        <Select 
          value={item.category} 
          onValueChange={(value) => {
            const newCategoryFoods = foodsLibrary[value] || [];
            const defaultFood = newCategoryFoods[0];
            
            if (value === 'custom') {
              onChange({
                ...item,
                category: 'custom',
                food_id: 'custom',
                custom_name: item.custom_name || '',
                amount: item.amount || 100,
                unit: item.unit || 'g',
                manual_macros: undefined
              });
            } else {
              onChange({
                ...item,
                category: value,
                food_id: defaultFood?.id || '',
                custom_name: undefined,
                amount: defaultFood?.default_amount || 100,
                unit: defaultFood?.unit || 'g',
                manual_macros: undefined
              });
            }
          }}
        >
          <SelectTrigger className={cn(
            "w-[90px] h-8 text-[10px] border-none",
            category?.bgColor || "bg-[#1A1A1A]"
          )}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
            {CATEGORY_ORDER.map((catId) => {
              const cat = getCategoryInfo(catId);
              const hasFoods = (foodsLibrary[catId] || []).length > 0;
              return (
                <SelectItem 
                  key={catId} 
                  value={catId} 
                  className={cn("text-white hover:bg-[#2A2A2A] text-xs", cat.color)}
                  disabled={!hasFoods && catId !== 'custom'}
                >
                  {cat.label}
                </SelectItem>
              );
            })}
            <SelectItem value="custom" className="text-white hover:bg-[#2A2A2A] text-xs text-gray-400">
              + Manual
            </SelectItem>
          </SelectContent>
        </Select>

        {/* Alimento */}
        {isManualEntry || isLegacyItem ? (
          <Input
            value={item.custom_name || ''}
            onChange={(e) => onChange({ ...item, custom_name: e.target.value })}
            placeholder="Nombre del alimento..."
            className="flex-1 h-8 bg-[#1A1A1A] border-[#2A2A2A] text-white text-xs"
          />
        ) : (
          <Select 
            value={item.food_id} 
            onValueChange={(value) => {
              const food = categoryFoods.find(f => f.id === value);
              onChange({
                ...item,
                food_id: value,
                amount: food?.default_amount || item.amount,
                unit: food?.unit || 'g',
                manual_macros: undefined
              });
            }}
          >
            <SelectTrigger className="flex-1 h-8 bg-[#1A1A1A] border-[#2A2A2A] text-white text-xs">
              <SelectValue placeholder="Seleccionar..." />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A] max-h-[200px]">
              {categoryFoods.map((food) => (
                <SelectItem key={food.id} value={food.id} className="text-white hover:bg-[#2A2A2A] text-xs">
                  {food.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Cantidad */}
        <div className="flex items-center gap-1">
          <Input
            type="number"
            value={item.amount}
            onChange={(e) => onChange({ ...item, amount: parseInt(e.target.value) || 0 })}
            className="w-14 h-8 bg-[#1A1A1A] border-[#2A2A2A] text-white text-xs text-center"
          />
          <span className="text-[#666] text-[10px] w-6">
            {item.unit === 'uds' ? 'uds' : item.unit === 'ml' ? 'ml' : 'g'}
          </span>
        </div>

        {/* Equivalents button */}
        {!isManualEntry && !isLegacyItem && (
          <button
            onClick={() => setShowEquivalents(true)}
            className="p-1.5 text-[#555] hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded transition-colors"
            title="Ver sustituciones equivalentes"
          >
            <RefreshCcw className="w-3 h-3" />
          </button>
        )}

        {/* Toggle manual macros */}
        <button
          onClick={() => setShowManualMacros(!showManualMacros)}
          className={cn(
            "p-1.5 rounded transition-colors",
            showManualMacros || item.manual_macros
              ? "text-[#D4AF37] bg-[#D4AF37]/10" 
              : "text-[#555] hover:bg-[#1A1A1A]"
          )}
          title="Editar macros manualmente"
        >
          <Calculator className="w-3 h-3" />
        </button>

        {/* Eliminar */}
        <button onClick={onDelete} className="p-1.5 text-red-400 hover:bg-red-500/10 rounded">
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
      
      {/* Calculated macros display */}
      <div className="flex items-center justify-between pl-1">
        <div className="flex items-center gap-2 text-[10px]">
          <Flame className="w-3 h-3 text-[#D4AF37]" />
          <span className="text-[#D4AF37] font-medium">{itemMacros.calories}</span>
          <span className="text-[#555]">kcal</span>
          <span className="text-[#333]">|</span>
          <span className="text-red-400">{itemMacros.protein}P</span>
          <span className="text-yellow-400">{itemMacros.carbs}C</span>
          <span className="text-blue-400">{itemMacros.fat}G</span>
          {item.manual_macros && (
            <span className="text-[#D4AF37] text-[9px]">(manual)</span>
          )}
        </div>
        {item.unit === 'g' && (
          <span className="text-[#444] text-[9px]">crudo</span>
        )}
      </div>

      {/* Manual macros editor */}
      {showManualMacros && (
        <div className="pt-2 border-t border-[#2A2A2A]">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[#888] text-[10px]">Sobrescribir macros:</span>
            {item.manual_macros && (
              <button onClick={clearManualMacros} className="text-[10px] text-red-400 hover:underline">
                Usar cálculo auto
              </button>
            )}
          </div>
          <div className="grid grid-cols-4 gap-2">
            <div>
              <label className="text-[#555] text-[9px]">kcal</label>
              <Input
                type="number"
                value={item.manual_macros?.calories || ''}
                onChange={(e) => handleManualMacroChange('calories', e.target.value)}
                placeholder={itemMacros.calories.toString()}
                className="h-7 bg-[#1A1A1A] border-[#2A2A2A] text-[#D4AF37] text-xs"
              />
            </div>
            <div>
              <label className="text-[#555] text-[9px]">Prot</label>
              <Input
                type="number"
                step="0.1"
                value={item.manual_macros?.protein || ''}
                onChange={(e) => handleManualMacroChange('protein', e.target.value)}
                placeholder={itemMacros.protein.toString()}
                className="h-7 bg-[#1A1A1A] border-[#2A2A2A] text-red-400 text-xs"
              />
            </div>
            <div>
              <label className="text-[#555] text-[9px]">Carb</label>
              <Input
                type="number"
                step="0.1"
                value={item.manual_macros?.carbs || ''}
                onChange={(e) => handleManualMacroChange('carbs', e.target.value)}
                placeholder={itemMacros.carbs.toString()}
                className="h-7 bg-[#1A1A1A] border-[#2A2A2A] text-yellow-400 text-xs"
              />
            </div>
            <div>
              <label className="text-[#555] text-[9px]">Grasa</label>
              <Input
                type="number"
                step="0.1"
                value={item.manual_macros?.fat || ''}
                onChange={(e) => handleManualMacroChange('fat', e.target.value)}
                placeholder={itemMacros.fat.toString()}
                className="h-7 bg-[#1A1A1A] border-[#2A2A2A] text-blue-400 text-xs"
              />
            </div>
          </div>
        </div>
      )}

      {/* Equivalents modal */}
      <EquivalentsModal
        open={showEquivalents}
        onClose={() => setShowEquivalents(false)}
        item={item}
        foodsLibrary={foodsLibrary}
        onSelectEquivalent={handleSelectEquivalent}
      />
    </div>
  );
};

// Meal Option Editor
const MealOptionEditor = ({ label, items, onChange, color, foodsLibrary }) => {
  const totals = useMemo(() => calculateTotalMacros(items, foodsLibrary), [items, foodsLibrary]);
  
  const createEmptyFoodItem = (categoryId = 'proteinas') => {
    const categoryFoods = foodsLibrary[categoryId] || [];
    const defaultFood = categoryFoods[0];
    
    if (categoryId === 'custom') {
      return {
        id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
        category: 'custom',
        food_id: 'custom',
        custom_name: '',
        amount: 100,
        unit: 'g'
      };
    }
    
    return {
      id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
      category: categoryId,
      food_id: defaultFood?.id || '',
      amount: defaultFood?.default_amount || 100,
      unit: defaultFood?.unit || 'g'
    };
  };

  const addItem = (categoryId = 'proteinas') => {
    const newItem = createEmptyFoodItem(categoryId);
    onChange([...items, newItem]);
  };

  const updateItem = (index, updated) => {
    const newItems = [...items];
    newItems[index] = updated;
    onChange(newItems);
  };

  const deleteItem = (index) => {
    onChange(items.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className={cn("text-xs font-semibold", color)}>{label}</span>
        <div className="flex items-center gap-2">
          <span className="text-[#555] text-[10px]">{items.length} items</span>
          {items.length > 0 && (
            <div className="flex items-center gap-1 text-[10px] px-2 py-0.5 bg-[#1A1A1A] rounded">
              <span className="text-[#D4AF37] font-medium">{totals.calories}</span>
              <span className="text-[#555]">kcal</span>
            </div>
          )}
        </div>
      </div>
      
      <div className="space-y-2">
        {items.map((item, idx) => (
          <FoodItemEditor
            key={item.id || idx}
            item={item}
            onChange={(updated) => updateItem(idx, updated)}
            onDelete={() => deleteItem(idx)}
            foodsLibrary={foodsLibrary}
          />
        ))}
      </div>

      {/* Add buttons */}
      <div className="flex flex-wrap gap-1 pt-1">
        {(foodsLibrary.proteinas || []).length > 0 && (
          <button onClick={() => addItem('proteinas')} className="px-2 py-1 text-[10px] bg-red-500/10 text-red-400 rounded hover:bg-red-500/20">+ Proteína</button>
        )}
        {(foodsLibrary.carbohidratos || []).length > 0 && (
          <button onClick={() => addItem('carbohidratos')} className="px-2 py-1 text-[10px] bg-yellow-500/10 text-yellow-400 rounded hover:bg-yellow-500/20">+ Carbo</button>
        )}
        {(foodsLibrary.grasas_saludables || []).length > 0 && (
          <button onClick={() => addItem('grasas_saludables')} className="px-2 py-1 text-[10px] bg-blue-500/10 text-blue-400 rounded hover:bg-blue-500/20">+ Grasa</button>
        )}
        {(foodsLibrary.verduras || []).length > 0 && (
          <button onClick={() => addItem('verduras')} className="px-2 py-1 text-[10px] bg-green-500/10 text-green-400 rounded hover:bg-green-500/20">+ Verdura</button>
        )}
        {(foodsLibrary.lacteos || []).length > 0 && (
          <button onClick={() => addItem('lacteos')} className="px-2 py-1 text-[10px] bg-cyan-500/10 text-cyan-400 rounded hover:bg-cyan-500/20">+ Lácteo</button>
        )}
        {(foodsLibrary.frutas || []).length > 0 && (
          <button onClick={() => addItem('frutas')} className="px-2 py-1 text-[10px] bg-orange-500/10 text-orange-400 rounded hover:bg-orange-500/20">+ Fruta</button>
        )}
        <button onClick={() => addItem('custom')} className="px-2 py-1 text-[10px] bg-gray-500/10 text-gray-400 rounded hover:bg-gray-500/20">+ Manual</button>
      </div>
    </div>
  );
};

// Main Meal Editor Component
const AdvancedMealEditor = ({ 
  meal, 
  onChange, 
  onDelete, 
  onDuplicate,
  onSaveAsTemplate,
  onMacrosChange 
}) => {
  const [expanded, setExpanded] = useState(false);
  const [activeOption, setActiveOption] = useState('A');
  const [foodsLibrary, setFoodsLibrary] = useState({});
  const [loading, setLoading] = useState(true);
  const { getAuthHeaders } = useAuth();

  const loadFoods = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/foods/active`, { headers: getAuthHeaders() });
      setFoodsLibrary(response.data.by_category || {});
    } catch (error) {
      console.error("Error loading foods:", error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    loadFoods();
  }, [loadFoods]);

  const itemsA = meal.items_a || [];
  const itemsB = meal.items_b || [];
  
  const mealMacros = useMemo(() => {
    const totalsA = calculateTotalMacros(itemsA, foodsLibrary);
    const totalsB = calculateTotalMacros(itemsB, foodsLibrary);
    return { optionA: totalsA, optionB: totalsB };
  }, [itemsA, itemsB, foodsLibrary]);

  useEffect(() => {
    if (onMacrosChange && !loading) {
      onMacrosChange(mealMacros);
    }
  }, [mealMacros, onMacrosChange, loading]);

  // Copy Option A to Option B
  const handleCopyAtoB = () => {
    const copiedItems = itemsA.map(item => ({
      ...item,
      id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString() + Math.random()
    }));
    onChange({ ...meal, items_b: copiedItems });
    toast.success("Opción A copiada a Opción B");
  };

  return (
    <div className="border border-[#2A2A2A] rounded-lg overflow-hidden" data-testid="meal-editor">
      {/* Header */}
      <div 
        className="flex items-center justify-between p-3 bg-[#1A1A1A] cursor-pointer hover:bg-[#222]"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <GripVertical className="w-4 h-4 text-[#444] cursor-grab flex-shrink-0" />
          
          <Input
            value={meal.time || "12:00"}
            onChange={(e) => { e.stopPropagation(); onChange({...meal, time: e.target.value}); }}
            onClick={(e) => e.stopPropagation()}
            className="w-14 h-7 bg-[#121212] border-[#2A2A2A] text-white text-xs text-center"
            placeholder="HH:MM"
          />
          
          <Input
            value={meal.name || ""}
            onChange={(e) => { e.stopPropagation(); onChange({...meal, name: e.target.value}); }}
            onClick={(e) => e.stopPropagation()}
            className="w-28 sm:w-36 h-7 bg-[#121212] border-[#2A2A2A] text-white text-xs font-medium"
            placeholder="Nombre comida"
          />
          
          {!expanded && itemsA.length > 0 && (
            <div className="hidden sm:flex items-center gap-1 text-[10px] px-2 py-1 bg-[#D4AF37]/10 rounded">
              <Flame className="w-3 h-3 text-[#D4AF37]" />
              <span className="text-[#D4AF37] font-medium">{mealMacros.optionA.calories}</span>
              <span className="text-[#666]">kcal</span>
              <span className="text-red-400">{mealMacros.optionA.protein}P</span>
              <span className="text-yellow-400">{mealMacros.optionA.carbs}C</span>
              <span className="text-blue-400">{mealMacros.optionA.fat}G</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1">
          <div className="flex items-center gap-1 mr-1">
            <span className="px-1.5 py-0.5 text-[10px] bg-[#D4AF37]/10 text-[#D4AF37] rounded">A:{itemsA.length}</span>
            <span className="px-1.5 py-0.5 text-[10px] bg-[#888]/10 text-[#888] rounded">B:{itemsB.length}</span>
          </div>
          
          {/* Duplicate meal button */}
          {onDuplicate && (
            <button 
              onClick={(e) => { e.stopPropagation(); onDuplicate(); }} 
              className="p-1.5 text-[#666] hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded"
              title="Duplicar comida"
            >
              <Layers className="w-4 h-4" />
            </button>
          )}
          
          {/* Save as template button */}
          {onSaveAsTemplate && (
            <button 
              onClick={(e) => { e.stopPropagation(); onSaveAsTemplate(); }} 
              className="p-1.5 text-[#666] hover:text-green-400 hover:bg-green-500/10 rounded"
              title="Guardar como plantilla"
            >
              <BookmarkPlus className="w-4 h-4" />
            </button>
          )}
          
          <button 
            onClick={(e) => { e.stopPropagation(); onDelete(); }} 
            className="p-1.5 text-red-400 hover:bg-red-500/10 rounded"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          
          {expanded ? <ChevronUp className="w-4 h-4 text-[#666]" /> : <ChevronDown className="w-4 h-4 text-[#666]" />}
        </div>
      </div>
      
      {/* Expanded content */}
      {expanded && (
        <div className="p-3 bg-[#121212] space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <RefreshCw className="w-4 h-4 text-[#D4AF37] animate-spin" />
              <span className="ml-2 text-[#666] text-xs">Cargando alimentos...</span>
            </div>
          ) : (
            <>
              {/* Tabs */}
              <div className="flex gap-2 border-b border-[#2A2A2A] pb-2">
                <button
                  onClick={() => setActiveOption('A')}
                  className={cn(
                    "px-3 py-1.5 text-xs font-semibold rounded-t transition-colors",
                    activeOption === 'A' 
                      ? "bg-[#D4AF37] text-[#0B0B0B]" 
                      : "bg-[#1A1A1A] text-[#888] hover:text-white"
                  )}
                >
                  Opción A ({itemsA.length})
                </button>
                <button
                  onClick={() => setActiveOption('B')}
                  className={cn(
                    "px-3 py-1.5 text-xs font-semibold rounded-t transition-colors",
                    activeOption === 'B' 
                      ? "bg-[#888] text-[#0B0B0B]" 
                      : "bg-[#1A1A1A] text-[#666] hover:text-white"
                  )}
                >
                  Opción B ({itemsB.length})
                </button>
              </div>

              {/* Editor */}
              {activeOption === 'A' ? (
                <MealOptionEditor
                  label="OPCIÓN A (Principal)"
                  items={itemsA}
                  onChange={(newItems) => onChange({...meal, items_a: newItems})}
                  color="text-[#D4AF37]"
                  foodsLibrary={foodsLibrary}
                />
              ) : (
                <MealOptionEditor
                  label="OPCIÓN B (Alternativa)"
                  items={itemsB}
                  onChange={(newItems) => onChange({...meal, items_b: newItems})}
                  color="text-[#888]"
                  foodsLibrary={foodsLibrary}
                />
              )}

              {/* Totals with copy button */}
              {(itemsA.length > 0 || itemsB.length > 0) && (
                <MealTotalsSummary 
                  itemsA={itemsA} 
                  itemsB={itemsB} 
                  foodsLibrary={foodsLibrary}
                  onCopyAtoB={handleCopyAtoB}
                />
              )}
            </>
          )}

          {/* Notes */}
          <div>
            <label className="text-[#666] text-xs block mb-1">Notas para el cliente</label>
            <Textarea
              value={meal.notes || ""}
              onChange={(e) => onChange({...meal, notes: e.target.value})}
              className="bg-[#1A1A1A] border-[#2A2A2A] text-white text-xs min-h-[60px]"
              placeholder="Ej: Pesos en crudo. Cocinar con spray antiadherente..."
            />
          </div>
        </div>
      )}
    </div>
  );
};

export { calculateTotalMacros, calculateItemMacros };
export default AdvancedMealEditor;
